import { isEqual } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMarketDataMarketSelectorItems } from '../../../components/MarketSelector/MarketData/useMarketDataMarketSelectorItems';
import { useMarketSelectorAggregationPresets } from '../../../components/MarketSelector/presets/useMarketSelectorAggregationPresets';
import { getSelectionsFromPreset } from '../../../components/MarketSelector/useMarketSelector';
import { useTradingSettings } from '../../../providers/TradingSettingsContext';

interface UseHydrateDeepDiveMarketsParams {
  symbol: string | undefined;
  selectedAggregation: string | undefined;
  selectedMarketAccounts: string[];
  setSelectedAggregation: (value: string | undefined) => void;
  setSelectedMarketAccounts: (value: string[]) => void;
}

/**
 * This hook keeps the selectedMarketAccounts property of a DeepDive tab in sync with the derived markets from an aggregation selection.
 *
 * This hook will constantly validate the current selectedMarketAccounts being used in the page and make sure that they correctly
 * reflect the aggregation. The hook does not return anything, instead it just calls `save` with the correct state, which then
 * updates the tab further up and ends up updating the DeepDive page.
 *
 */
export const useHydrateDeepDiveMarkets = ({
  symbol,
  selectedAggregation,
  selectedMarketAccounts,
  setSelectedAggregation,
  setSelectedMarketAccounts,
}: UseHydrateDeepDiveMarketsParams) => {
  const { useNewMarketSelector } = useTradingSettings();

  const { aggregationPresets } = useMarketSelectorAggregationPresets();

  const items = useMarketDataMarketSelectorItems({
    symbol,
    queryMSSRecords: false,
  });
  const itemsMap = useMemo(() => new Map(items.map(item => [item.name, item])), [items]);
  const itemsKey = useMemo(() => items.map(i => i.name).join('|'), [items]);

  const setAggregationRef = useRef(setSelectedAggregation);
  setAggregationRef.current = setSelectedAggregation;
  const setMarketAccountsRef = useRef(setSelectedMarketAccounts);
  setMarketAccountsRef.current = setSelectedMarketAccounts;

  const hydrate = useCallback(() => {
    if (!useNewMarketSelector || !selectedAggregation) {
      return;
    }

    if (!aggregationPresets) {
      return;
    }

    const agg = aggregationPresets.find(a => a.id === selectedAggregation);
    if (!agg) {
      setAggregationRef.current(undefined);
      return;
    }

    const hydratedSelections = getSelectionsFromPreset(agg, itemsMap).map(item => item.name);

    if (isEqual(selectedMarketAccounts, hydratedSelections)) {
      return;
    }

    setMarketAccountsRef.current(hydratedSelections);
  }, [useNewMarketSelector, selectedAggregation, aggregationPresets, itemsMap, selectedMarketAccounts]);

  const hydrateRef = useRef(hydrate);
  hydrateRef.current = hydrate;

  const hydrateKey =
    useNewMarketSelector && selectedAggregation && aggregationPresets
      ? [
          symbol ?? '',
          selectedAggregation,
          itemsKey,
          selectedMarketAccounts.join('|'),
          aggregationPresets.map(p => `${p.id}:${p.itemIDs.join(',')}`).join('|'),
        ].join('|')
      : '';

  useEffect(() => {
    if (!hydrateKey) {
      return;
    }

    let cancelled = false;

    queueMicrotask(() => {
      if (cancelled) {
        return;
      }
      hydrateRef.current();
    });

    return () => {
      cancelled = true;
    };
  }, [hydrateKey]);
};
